import React, { Suspense, lazy, useState } from "react";
import { BookOpen, FileText, Maximize2 } from "lucide-react";
import { AppButton, MathText, cn } from "../ui";
import Shimmer from "../Common/Shimmer";
import ImageLightbox from "../ExamRunner/ImageLightbox";

// pdf.js is large and most papers have no document: it loads the first time a
// PDF passage is actually shown, not with the exam screen.
const PassagePdf = lazy(() => import("./PassagePdf"));

/**
 * PassageView — the passage a set of questions is asked about: a reading
 * passage with an optional figure, or a document. Shown beside the question in
 * the exam, above the set in the result review, and as the editor's preview.
 *
 * Props:
 *   passage     — { _id, title, kind: 'text'|'pdf', instructions, text, imageUrl, file: { name } | null }
 *   loadFileUrl — () => Promise<string>, for a PDF passage
 *   cacheKey    — keeps a PDF's bytes across remounts (defaults to the passage id)
 *   rangeLabel  — "Questions 3–7" — which questions are about it
 *   selectable  — false when the exam blocks copying
 *   fontScale   — the exam's text-size setting
 *   headingLevel — 2 (default) or 3, to sit correctly in the page's outline
 */

// "$" is also money ("charges of $250 and $300"), and treating the span between
// two of them as LaTeX would mangle a billing passage. Only text that looks like
// it was written FOR math goes through MathText.
const LOOKS_LIKE_MATH = /\$\$[\s\S]+?\$\$|\$[^$\n]*[\\^_{}][^$\n]*\$/;

export default function PassageView({
  passage,
  loadFileUrl,
  cacheKey,
  rangeLabel,
  selectable = true,
  fontScale = 1,
  headingLevel = 2,
  className,
}) {
  const [zoomed, setZoomed] = useState(false);
  if (!passage) return null;

  const isPdf = passage.kind === "pdf";
  const Heading = headingLevel === 3 ? "h3" : "h2";
  const Icon = isPdf ? FileText : BookOpen;
  const text = passage.text || "";

  return (
    <section
      aria-label={passage.title ? `Passage: ${passage.title}` : "Passage"}
      className={cn("tw-flex tw-flex-col tw-gap-3", !selectable && "tw-select-none", className)}
    >
      <header className="tw-flex tw-flex-col tw-gap-0.5">
        {rangeLabel && (
          <span className="tw-text-xs tw-font-medium tw-uppercase tw-tracking-[0.04em] tw-text-primary">
            {rangeLabel}
          </span>
        )}
        <Heading className="tw-m-0 tw-flex tw-items-start tw-gap-2 tw-text-[0.9375rem] tw-font-semibold tw-leading-snug tw-text-content">
          <Icon size={16} className="tw-mt-0.5 tw-shrink-0 tw-text-content-muted" aria-hidden="true" />
          <span className="tw-min-w-0 tw-break-words">{passage.title || (isPdf ? "Document" : "Passage")}</span>
        </Heading>
      </header>

      {passage.instructions && (
        <p className="tw-m-0 tw-whitespace-pre-line tw-text-sm tw-italic tw-leading-relaxed tw-text-content-muted">
          {passage.instructions}
        </p>
      )}

      {isPdf ? (
        passage.file && loadFileUrl ? (
          <Suspense fallback={<Shimmer height={360} />}>
            <PassagePdf
              loadUrl={loadFileUrl}
              cacheKey={cacheKey || String(passage._id)}
              fileName={passage.file.name}
              selectable={selectable}
            />
          </Suspense>
        ) : (
          <p className="tw-m-0 tw-text-sm tw-text-content-muted">The document is not available here.</p>
        )
      ) : (
        <>
          {text && (
            <div
              className="tw-whitespace-pre-line tw-break-words tw-leading-[1.75] tw-text-content"
              style={{ fontSize: `${0.9375 * fontScale}rem` }}
            >
              {LOOKS_LIKE_MATH.test(text) ? <MathText as="div" text={text} /> : text}
            </div>
          )}
          {passage.imageUrl && (
            <figure className="tw-m-0 tw-flex tw-flex-col tw-items-start tw-gap-2">
              <img
                src={passage.imageUrl}
                alt=""
                className="tw-max-h-96 tw-max-w-full tw-rounded-sm tw-border tw-border-solid tw-border-line tw-bg-surface tw-object-contain"
              />
              <AppButton type="button" variant="ghost" size="sm" onClick={() => setZoomed(true)}>
                <Maximize2 size={16} aria-hidden="true" /> View full size
              </AppButton>
            </figure>
          )}
        </>
      )}

      {zoomed && passage.imageUrl && (
        <ImageLightbox
          src={passage.imageUrl}
          alt={`Figure for ${passage.title || "the passage"}`}
          onClose={() => setZoomed(false)}
        />
      )}
    </section>
  );
}
